import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import type { InlineTextBlockAttsInterface } from '~/types/AppData/Blocks/InlineTextBlockInterface';
import { cva } from 'class-variance-authority';
import { useIsPremiumTemplate } from '~/utilities/useIsPremiumTemplate';
import { useBlockClasses } from '~/utilities/useBlockClasses';
import { getCSSColorChildLinks } from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';

const cssClasses = cva('', {
  variants: {
    element: {
      h2: 'scroll-mt-24',
      ol: '!list-decimal ml-5',
      ul: '!list-disc ml-5',
    },
    dropcap: {
      true: '!mt-[30px] first-letter:float-left first-letter:text-[48px] first-letter:leading-[48px] md:first-letter:text-[54px] md:first-letter:leading-[54px] first-letter:pr-1.5',
    },
    marginTop: {
      true: 'my-[18px]',
      false: 'mt-[18px] md:mt-0 mb-[18px]',
    },
    color: getCSSColorChildLinks(),
  },
});

/**
 * Strips shortcodes from a given content string.
 *
 * @param {string} content - The content string containing possible shortcodes.
 * @returns {string} - The content string with all shortcodes removed.
 */
const stripShortcodes = (content: string): string => {
  const shortcodeRegex =
    /\[(\[?)(table)(?![\w-])([^\]]*?)(\/)?\](?:([^[]*?)\[\/\2\])?(\]?)/g;
  return content.replace(shortcodeRegex, '');
};

// Can use something like cheerio if we need better parsing: https://github.com/cheeriojs/cheerio
export default component$(
  ({ html, index }: InlineTextBlockAttsInterface): JSX.Element => {
    if (!html) return <></>;

    const isPremium = useIsPremiumTemplate();
    const blockClasses = useBlockClasses();

    // Regular expression to capture the tag and inner HTML
    const regex = /<(\w+)(?:\s+[^>]*)?>([\s\S]*?)<\/\1>/g;
    const matches = [...html.matchAll(regex)];
    const {
      themeComponents: { articleBlock },
    } = useContext(AppDataContext);

    return matches.map((match, i) => {
      const Component = match[1] as any as JSX.ElementType;
      const strippedContent = stripShortcodes(match[2]);

      if (!strippedContent) return <></>;

      return (
        <Component
          key={i}
          class={cssClasses({
            element: match[1] as any,
            dropcap: index === 0 && isPremium,
            class: blockClasses,
            marginTop: index !== 0,
            color: articleBlock.paragraphLinkColor.value,
          })}
          id={`inline-text-${index}`}
          dangerouslySetInnerHTML={strippedContent}
        />
      );
    });
  }
);
